"use client"

import { useState } from "react"
import {
  Play,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Euro,
} from "lucide-react"

/**
 * Client component for /admin/sync.
 *
 * Renders two big admin buttons:
 *   • "Synchroniser les galeries" → POST /api/admin/sync
 *     Re-scrapes adis95.fr for missing product images. Idempotent.
 *   • "Re-seeder les prix" → POST /api/admin/reseed-prices
 *     Walks every product and fills any NULL price from the
 *     researched market target list (lib/product-prices.ts). With
 *     the "Forcer" switch on, every price is refreshed (±8% jitter).
 *
 * Both endpoints are admin-session-gated at the API layer; this UI
 * only orchestrates the request and renders a result card. Styling
 * uses Payload's CSS variables (`var(--theme-*)`) + inline styles so
 * the panel sits cleanly inside the admin chrome without dragging in
 * shadcn/storefront tokens.
 */

type SyncResult = {
  totalProducts: number
  enriched: number
  unchanged: number
  totalImagesAdded: number
  perProduct: Array<{ slug: string; before: number; after: number }>
  errors: Array<{ slug: string; reason: string }>
  durationMs: number
}

type SyncResponse =
  | {
      ok: true
      ranAt: string
      adminEmail: string | null
      params: { minTarget: number; maxPerProduct: number }
      result: SyncResult
      log: string[]
    }
  | {
      ok: false
      error: string
      log?: string[]
    }

type PriceResult = {
  totalProducts: number
  updated: number
  skipped: number
  forced: boolean
  perProduct: Array<{ slug: string; before: number | null; after: number }>
  durationMs: number
}

type PriceResponse =
  | {
      ok: true
      ranAt: string
      adminEmail: string | null
      params: { force: boolean }
      result: PriceResult
      log: string[]
    }
  | {
      ok: false
      error: string
      log?: string[]
    }

export function SyncClient({ adminEmail }: { adminEmail: string }) {
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<SyncResponse | null>(null)
  const [minTarget, setMinTarget] = useState(2)
  const [maxPerProduct, setMaxPerProduct] = useState(8)
  const [priceRunning, setPriceRunning] = useState(false)
  const [priceResult, setPriceResult] = useState<PriceResponse | null>(null)
  const [priceForce, setPriceForce] = useState(false)

  async function handleSync() {
    setRunning(true)
    setResult(null)
    try {
      const res = await fetch("/api/admin/sync", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ minTarget, maxPerProduct }),
        credentials: "include",
      })
      const json = (await res.json()) as SyncResponse
      setResult(json)
    } catch (err) {
      setResult({
        ok: false,
        error: `Requête impossible : ${(err as Error).message}`,
      })
    } finally {
      setRunning(false)
    }
  }

  async function handleReseedPrices() {
    setPriceRunning(true)
    setPriceResult(null)
    try {
      const res = await fetch("/api/admin/reseed-prices", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ force: priceForce }),
        credentials: "include",
      })
      const json = (await res.json()) as PriceResponse
      setPriceResult(json)
    } catch (err) {
      setPriceResult({
        ok: false,
        error: `Requête impossible : ${(err as Error).message}`,
      })
    } finally {
      setPriceRunning(false)
    }
  }

  const card: React.CSSProperties = {
    border: "1px solid var(--theme-elevation-150, rgba(0,0,0,0.08))",
    background: "var(--theme-elevation-50, rgba(0,0,0,0.02))",
    borderRadius: "0.5rem",
    padding: "1.25rem",
    marginBottom: "1rem",
  }

  return (
    <div>
      <h1 style={{ marginBottom: "0.25rem" }}>
        Synchronisation manuelle des galeries
      </h1>
      <p style={{ color: "var(--theme-elevation-500, #6b7280)", marginBottom: "1.5rem" }}>
        Re-scrappe le site WooCommerce historique{" "}
        <strong>adis95.fr</strong> pour télécharger les images de galerie
        manquantes sur les fiches produits Payload. Connecté en tant que{" "}
        <code>{adminEmail || "(inconnu)"}</code>.
      </p>

      <div style={card}>
        <h2 style={{ fontSize: "1rem", marginTop: 0 }}>Paramètres</h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "0.75rem",
            marginTop: "0.5rem",
          }}
        >
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: "0.875rem" }}>
              Cible minimale d&apos;images par produit
            </span>
            <input
              type="number"
              min={1}
              max={8}
              value={minTarget}
              onChange={(e) =>
                setMinTarget(
                  Math.max(1, Math.min(8, Number(e.target.value) || 2)),
                )
              }
              disabled={running}
              style={{
                padding: "0.45rem 0.6rem",
                borderRadius: 6,
                border: "1px solid var(--theme-elevation-150, #e5e7eb)",
                background: "var(--theme-elevation-0, #fff)",
                color: "inherit",
              }}
            />
            <span style={{ fontSize: "0.75rem", color: "var(--theme-elevation-500, #6b7280)" }}>
              Les produits qui ont déjà ≥ cette valeur sont ignorés.
            </span>
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: "0.875rem" }}>
              Plafond d&apos;images téléchargées par produit
            </span>
            <input
              type="number"
              min={1}
              max={12}
              value={maxPerProduct}
              onChange={(e) =>
                setMaxPerProduct(
                  Math.max(1, Math.min(12, Number(e.target.value) || 8)),
                )
              }
              disabled={running}
              style={{
                padding: "0.45rem 0.6rem",
                borderRadius: 6,
                border: "1px solid var(--theme-elevation-150, #e5e7eb)",
                background: "var(--theme-elevation-0, #fff)",
                color: "inherit",
              }}
            />
            <span style={{ fontSize: "0.75rem", color: "var(--theme-elevation-500, #6b7280)" }}>
              Garde-fou anti-vacuum sur le dossier WP.
            </span>
          </label>
        </div>
      </div>

      <button
        type="button"
        onClick={handleSync}
        disabled={running}
        style={{
          padding: "0.65rem 1.25rem",
          borderRadius: 8,
          border: "none",
          fontWeight: 600,
          fontSize: "0.95rem",
          cursor: running ? "not-allowed" : "pointer",
          background: running
            ? "var(--theme-elevation-200, #e5e7eb)"
            : "var(--theme-success-500, #16a34a)",
          color: running
            ? "var(--theme-elevation-600, #4b5563)"
            : "#fff",
          transition: "background 120ms ease",
        }}
      >
        {running ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Loader2 size={14} aria-hidden style={{ animation: "spin 1s linear infinite" }} />
            Synchronisation en cours
          </span>
        ) : (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Play size={14} aria-hidden />
            Synchroniser maintenant
          </span>
        )}
      </button>

      {result && result.ok ? (
        <div
          style={{
            ...card,
            marginTop: "1.5rem",
            background: "rgba(22,163,74,0.06)",
            border: "1px solid rgba(22,163,74,0.25)",
          }}
        >
          <h2
            style={{
              marginTop: 0,
              color: "var(--theme-success-700, #15803d)",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <CheckCircle2 size={18} aria-hidden />
            Synchronisation terminée
          </h2>
          <ul style={{ margin: "0.25rem 0 0.75rem 1.25rem", padding: 0 }}>
            <li>
              <strong>{result.result.enriched}</strong> produit(s) enrichi(s)
            </li>
            <li>
              <strong>+{result.result.totalImagesAdded}</strong> image(s)
              ajoutée(s)
            </li>
            <li>
              <strong>{result.result.unchanged}</strong> produit(s) inchangé(s)
            </li>
            <li>
              Durée : <strong>{Math.round(result.result.durationMs / 100) / 10} s</strong>
            </li>
            {result.result.errors.length > 0 ? (
              <li>
                Erreurs : <strong>{result.result.errors.length}</strong>
              </li>
            ) : null}
          </ul>

          {result.result.perProduct.length > 0 ? (
            <details>
              <summary
                style={{
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                }}
              >
                Voir les diff par produit ({result.result.perProduct.length})
              </summary>
              <ol style={{ marginTop: "0.5rem", fontSize: "0.85rem" }}>
                {result.result.perProduct.map((p) => (
                  <li key={p.slug}>
                    <code>{p.slug}</code>{" "}
                    <span style={{ color: "var(--theme-elevation-500, #6b7280)" }}>
                      ({p.before} -&gt; {p.after})
                    </span>
                  </li>
                ))}
              </ol>
            </details>
          ) : null}

          {result.result.errors.length > 0 ? (
            <details>
              <summary
                style={{
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  color: "var(--theme-error-600, #dc2626)",
                }}
              >
                Voir les erreurs ({result.result.errors.length})
              </summary>
              <ul style={{ marginTop: "0.5rem", fontSize: "0.85rem" }}>
                {result.result.errors.map((e, i) => (
                  <li key={i}>
                    <code>{e.slug}</code>: {e.reason}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </div>
      ) : null}

      {result && !result.ok ? (
        <div
          style={{
            ...card,
            marginTop: "1.5rem",
            background: "rgba(220,38,38,0.06)",
            border: "1px solid rgba(220,38,38,0.25)",
          }}
        >
          <h2
            style={{
              marginTop: 0,
              color: "var(--theme-error-700, #b91c1c)",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <AlertCircle size={18} aria-hidden />
            Échec
          </h2>
          <p style={{ margin: 0 }}>{result.error}</p>
        </div>
      ) : null}

      <div
        style={{
          marginTop: "2rem",
          fontSize: "0.8rem",
          color: "var(--theme-elevation-500, #6b7280)",
          borderTop: "1px solid var(--theme-elevation-100, rgba(0,0,0,0.06))",
          paddingTop: "1rem",
        }}
      >
        <p style={{ margin: "0 0 0.5rem 0" }}>
          <strong>Note :</strong> ce bouton est sûr à actionner plusieurs fois
          (idempotent). Aucune image existante n&apos;est supprimée. Les
          produits qui atteignent déjà la cible sont ignorés.
        </p>
      </div>

      {/* ── Re-seed prices panel ───────────────────────────────── */}
      <div style={{ marginTop: "2.5rem" }}>
        <h2 style={{ marginBottom: "0.25rem", fontSize: "1.1rem" }}>
          Re-seeder les prix produits
        </h2>
        <p style={{ color: "var(--theme-elevation-500, #6b7280)", marginBottom: "1rem" }}>
          Applique la liste de prix cibles (recherchée sur le marché français
          2025-2026) à chaque produit Payload.
        </p>

        <div style={card}>
          <label
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "0.75rem",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={priceForce}
              onChange={(e) => setPriceForce(e.target.checked)}
              disabled={priceRunning}
              style={{ marginTop: "0.2rem" }}
            />
            <span>
              <strong style={{ display: "block" }}>Forcer le re-seed</strong>
              <span style={{ fontSize: "0.85rem", color: "var(--theme-elevation-500, #6b7280)" }}>
                Décoché : seuls les produits sans prix sont mis à jour
                (sûr, idempotent). Coché : chaque produit reçoit une nouvelle
                valeur (±8% de jitter autour de la cible).
              </span>
            </span>
          </label>
        </div>

        <button
          type="button"
          onClick={handleReseedPrices}
          disabled={priceRunning}
          style={{
            padding: "0.65rem 1.25rem",
            borderRadius: 8,
            border: "none",
            fontWeight: 600,
            fontSize: "0.95rem",
            cursor: priceRunning ? "not-allowed" : "pointer",
            background: priceRunning
              ? "var(--theme-elevation-200, #e5e7eb)"
              : "var(--theme-success-500, #0f766e)",
            color: priceRunning
              ? "var(--theme-elevation-600, #4b5563)"
              : "#fff",
            transition: "background 120ms ease",
          }}
        >
          {priceRunning ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Loader2
                size={14}
                aria-hidden
                style={{ animation: "spin 1s linear infinite" }}
              />
              Re-seed en cours
            </span>
          ) : (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Euro size={14} aria-hidden />
              {priceForce ? "Re-seeder TOUS les prix" : "Remplir les prix manquants"}
            </span>
          )}
        </button>

        {priceResult && priceResult.ok ? (
          <div
            style={{
              ...card,
              marginTop: "1.5rem",
              background: "rgba(15,118,110,0.06)",
              border: "1px solid rgba(15,118,110,0.25)",
            }}
          >
            <h3
              style={{
                marginTop: 0,
                color: "var(--theme-success-700, #0f766e)",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <CheckCircle2 size={18} aria-hidden />
              Re-seed terminé
            </h3>
            <ul style={{ margin: "0.25rem 0 0.75rem 1.25rem", padding: 0 }}>
              <li>
                <strong>{priceResult.result.updated}</strong> prix mis à jour
              </li>
              <li>
                <strong>{priceResult.result.skipped}</strong> produit(s) inchangé(s)
              </li>
              <li>
                Mode :{" "}
                <strong>
                  {priceResult.result.forced ? "forcé (tous)" : "idempotent (manquants)"}
                </strong>
              </li>
              <li>
                Durée :{" "}
                <strong>{Math.round(priceResult.result.durationMs / 100) / 10} s</strong>
              </li>
            </ul>
            {priceResult.result.perProduct.length > 0 ? (
              <details>
                <summary
                  style={{
                    cursor: "pointer",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                  }}
                >
                  Voir les diff par produit ({priceResult.result.perProduct.length})
                </summary>
                <ol style={{ marginTop: "0.5rem", fontSize: "0.85rem" }}>
                  {priceResult.result.perProduct.map((p) => (
                    <li key={p.slug}>
                      <code>{p.slug}</code>{" "}
                      <span style={{ color: "var(--theme-elevation-500, #6b7280)" }}>
                        ({p.before ?? "∅"} € -&gt; {p.after} €)
                      </span>
                    </li>
                  ))}
                </ol>
              </details>
            ) : null}
          </div>
        ) : null}

        {priceResult && !priceResult.ok ? (
          <div
            style={{
              ...card,
              marginTop: "1.5rem",
              background: "rgba(220,38,38,0.06)",
              border: "1px solid rgba(220,38,38,0.25)",
            }}
          >
            <h3
              style={{
                marginTop: 0,
                color: "var(--theme-error-700, #b91c1c)",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <AlertCircle size={18} aria-hidden />
              Échec
            </h3>
            <p style={{ margin: 0 }}>{priceResult.error}</p>
          </div>
        ) : null}
      </div>
    </div>
  )
}
